import React, { JSX, lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { TranslationProvider } from './contexts/TranslationContext';
import { EnterpriseTranslationProvider } from './contexts/EnterpriseTranslationContext';
import { ToastProvider } from './contexts/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import { cleanupExpiredQuote } from './utils/pendingQuoteStorage';

// Code splitting: Lazy load components to reduce initial bundle size
// This improves LCP (Largest Contentful Paint) by ~1.5s and reduces initial bundle by ~86KB
const LandingPage = lazy(() => import('./components/LandingPage'));
const TranslationApp = lazy(() => import('./components/TranslationApp'));
const TechnicalSupport = lazy(() => import('./components/TechnicalSupport'));
const EnterpriseTranslation = lazy(() => import('./components/EnterpriseTranslation'));
const DocumentsOrders = lazy(() => import('./components/DocumentsOrders'));
const EnterpriseDocuments = lazy(() => import('./components/EnterpriseDocuments'));
const EnterpriseInvoices = lazy(() => import('./components/EnterpriseInvoices'));
const Auth = lazy(() => import('./components/Auth'));
const CorporateLoginPage = lazy(() => import('./components/CorporateLoginPage'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const EnterpriseDemoPage = lazy(() => import('./components/EnterpriseDemoPage'));
const PlaceholderLegalPage = lazy(() => import('./components/PlaceholderLegalPage'));
const PaymentComplete = lazy(() => import('./components/PaymentComplete'));

/**
 * Loading fallback component shown while lazy-loaded routes are being fetched
 */
const RouteLoader: React.FC = () => (
    <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Loading page"
    >
        <div className="text-center">
            <div
                className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto"
                aria-hidden="true"
            />
            <span className="sr-only">Loading...</span>
            <p className="mt-4 text-gray-600">Loading...</p>
        </div>
    </div>
);

const App = (): JSX.Element => {
    useEffect(() => {
        cleanupExpiredQuote().catch(() => {}); // best-effort cleanup
    }, []);

    return (
        // ErrorBoundary catches React errors and prevents full app crashes
        <ErrorBoundary>
            <ToastProvider>
                <Router>
                    {/* Suspense shows loading fallback while components are being lazy-loaded */}
                    <Suspense fallback={<RouteLoader />}>
                        <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/login" element={<Auth />} />
                            <Route path="/signup" element={<Auth />} />
                            <Route path="/login/corporate" element={<CorporateLoginPage />} />
                            <Route path="/enterprise-demo" element={<EnterpriseDemoPage />} />
                            <Route
                                path="/translate"
                                element={
                                    <ProtectedRoute requireIndividual>
                                        <TranslationProvider>
                                            <TranslationApp />
                                        </TranslationProvider>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/documents-orders"
                                element={
                                    <ProtectedRoute requireCorporate>
                                        <DocumentsOrders />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/enterprise/documents"
                                element={
                                    <ProtectedRoute requireCorporate>
                                        <EnterpriseDocuments />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/enterprise/invoices"
                                element={
                                    <ProtectedRoute requireCorporate>
                                        <EnterpriseInvoices />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/enterprise-translation"
                                element={
                                    <ProtectedRoute requireCorporate>
                                        <EnterpriseTranslationProvider>
                                            <EnterpriseTranslation />
                                        </EnterpriseTranslationProvider>
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/terms" element={<PlaceholderLegalPage title="Terms of Service" />} />
                            <Route path="/privacy" element={<PlaceholderLegalPage title="Privacy Policy" />} />
                            <Route path="/support" element={<TechnicalSupport />} />
                            <Route path="/payment-complete" element={<PaymentComplete />} />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute requireAdmin>
                                        <AdminDashboard />
                                    </ProtectedRoute>
                                }
                            />
                        </Routes>
                    </Suspense>
                </Router>
            </ToastProvider>
        </ErrorBoundary>
    );
};

export default App;
